import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

import {
    handleRegisteredEmailVerify,
    handleResendOTPToMail,
} from "../../shared/controllers/authController";
import CodeInputForm from "../../utils/codeInputForm";

import authbg from "./assets/authbg.svg";
import blackload from "./assets/blackload.gif";
import load from "./assets/load.gif";
import logo from "./assets/logo.svg";

const VerifyMail: React.FC = () => {
    const [code, setCode] = useState<string[]>(new Array(6).fill("")); // Initialize with six empty strings
    const [error, setError] = useState<string>(""); // State to manage validation error
    const navigate = useNavigate();
    const userEmail = localStorage.getItem("user_email") || "";

    const [loading, setLoading] = useState<boolean>(false);
    const [loadingVerify, setLoadingVerify] = useState<boolean>(false);

    const maskEmail = (email: string | null): string => {
        if (!email || !email.includes("@")) return "";

        const [name = "", domain = ""] = email.split("@");
        if (!name || !domain) return "";
        const length = name.length;

        // Calculate how many characters to reveal (round up for odd lengths)
        const revealCount = Math.ceil(length / 2);
        const maskCount = length - revealCount;

        // Create the masked part
        const visiblePart = name.slice(0, revealCount);
        const maskedName = `${visiblePart}${"*".repeat(maskCount)}`;

        return `${maskedName}@${domain}`;
    };

    const maskedEmail = maskEmail(userEmail);

    const handleCodeChange = (updatedCode: string[]) => {
        setCode(updatedCode);
        setError(""); // Clear any previous errors when the user updates the code
    };

    // Validation function to check if the code is fully entered
    const validateFields = (): boolean => {
        if (code.some((digit) => digit.trim() === "")) {
            setError("Please enter all six digits.");
            return false;
        }
        if (!code.every((digit) => /^\d$/.test(digit))) {
            setError("Please enter only numbers.");
            return false;
        }
        return true;
    };

    // For resend
    const onSuccess = (_response: unknown) => {
        setLoading(false);
    };

    const onError = () => {
        setLoading(false);
    };

    const handleSend = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        setLoading(true);
        const userData = { email: userEmail };
        handleResendOTPToMail(userData, onSuccess, onError);
    };

    // For verification
    const onSuccessVerify = (_response: unknown) => {
        setLoadingVerify(false);
        navigate("/create-pin");
    };

    const onErrorVerify = () => {
        setLoadingVerify(false);
    };

    const handleVerify = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        if (validateFields()) {
            setLoadingVerify(true);
            const userData = { email: userEmail, otp: code.join("") }; // Join code array into a string
            handleRegisteredEmailVerify(userData, onSuccessVerify, onErrorVerify);
        }
    };

    return (
        <>
            <div
                style={{
                    backgroundImage: `url(${authbg})`,
                    backgroundPosition: "center",
                    backgroundSize: "cover",
                }}
                className="w-full md:w-[120%] h-full bg-[#1212128d] z-[999] fixed top-0 left-0 md:-left-[20%] p-6 flex justify-center items-center"
            >
                <div className="md:ml-[20%] h-[550px] bg-[#FFFFFF] p-6 rounded-[15px] w-full md:w-[550px]">
                    <div className="w-full h-full bg-[#fff] overflow-y-auto p-4 md:p-4 flex flex-col items-center">
                        <img src={logo} className=" w-[20%]" alt="" />
                        <p className=" mt-6 text-center font-Manrope font-bold text-[#272D37] text-2xl">
                            Verify Email
                        </p>
                        <p className=" text-center max-w-[400px] font-Manrope text-lg mt-3 text-[#272D37]">
                            Enter verification code sent to {maskedEmail}
                        </p>

                        <CodeInputForm code={code} onCodeChange={handleCodeChange} />
                        {error && (
                            <p className="text-[#DC3545] font-Manrope text-xs mt-2">
                                {error}
                            </p>
                        )}

                        <button
                            disabled={loadingVerify}
                            onClick={handleVerify}
                            className=" bg-[#317333] rounded-md w-full mt-6 py-3 font-Manrope text-[#fff] font-semibold text-base flex justify-center max-w-[400px] items-center"
                        >
                            {loadingVerify ? (
                                <img src={load} className=" w-5" alt="" />
                            ) : (
                                "Verify"
                            )}
                        </button>
                        <p className=" flex items-center justify-center mt-4 px-8 text-body14Regular font-Manrope text-[#595959] text-center">
                            Didn’t receive the code?{" "}
                            <button
                                disabled={loading}
                                onClick={handleSend}
                                className=" text-[#317333]"
                            >
                                {loading ? (
                                    <img src={blackload} className=" w-5 ml-2" alt="" />
                                ) : (
                                    " Send again"
                                )}
                            </button>
                        </p>
                    </div>
                </div>
            </div>
        </>
    );
};

export default VerifyMail;
